import React, { Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useTheme } from 'styled-components';
import { useBreakpoints } from 'hooks/useWindowSize';
import { useNavigate, useParams } from 'react-router';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';

import { Button } from 'shared/components/Button/Button';
import { BackButton } from 'modules/auth/components/BackButton';
import { AuthBox, AuthWrapper } from 'modules/auth/Auth.styles';
import { Header } from 'modules/auth/components/Header/Header';
import { PasswordField } from 'shared/components/inputs/PasswordField/PasswordField';
import { toast } from 'shared/components/Toast/Toast';

import { IFormInput } from 'modules/account/components/LoginDetailsForm/LoginDetailsForm';
import { MINIMUM_PASSWORD_LENGTH } from 'constants/auth';
import { submitCreatePassword } from 'api/accounts';
import { handlePasswordValidation } from 'modules/auth/Auth.utils';
import { pages } from 'pages';
import { getScoreWords } from 'modules/auth/components/PasswordForm/getScoreWords';
import { LoadingSpinner } from 'shared/components/LoadingSpinner';

const PasswordStrengthBar = React.lazy(() => import('react-password-strength-bar'));

type CreatePasswordFormInput = Omit<IFormInput, 'oldPassword'>;

export function CreatePassword() {
  const [passwordScore, setPasswordScore] = useState(0);
  const [showScore, setShowScore] = useState(false);

  const { token } = useParams();
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const screenSize = useBreakpoints();

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
    resetField,
  } = useForm<CreatePasswordFormInput>({ mode: 'onSubmit' });

  const watchNewPassword = useWatch({
    control,
    name: 'newPassword',
  });

  const { mutate } = useMutation({
    mutationFn: submitCreatePassword,
    onSuccess: () => {
      toast({
        type: 'success',
        title: t('toast.success'),
        message: t('account.successfullyUpdated'),
        theme,
      });
      reset();
      navigate(`/${pages.LOGIN}`, { replace: true });
    },
    onError: () => {
      toast({
        type: 'error',
        title: t('toast.error'),
        message: t('account.unableToSave'),
        theme,
      });
    },
  });

  const onSubmit: SubmitHandler<CreatePasswordFormInput> = ({ newPassword }) => {
    mutate({ password: newPassword, token: token || '' });
  };

  const scoreWords = getScoreWords(t);

  return (
    <AuthWrapper>
      <BackButton />
      <AuthBox>
        <Header
          title={t('forgotPasswordScreen.enterYourNew')}
          titleBold={t('forgotPasswordScreen.sendScreenHeaderTwo')}
          showLogo
          headerAlwaysCentered
        />
        <form onSubmit={handleSubmit(onSubmit)}>
          <div>
            <PasswordField
              register={register}
              onClearInput={resetField}
              hasError={Boolean(errors.newPassword)}
              errorMessage={errors.newPassword?.message}
              fieldName="newPassword"
              label={t('account.newPassword')}
              showPlaceholder={false}
              onValidate={(value) =>
                handlePasswordValidation({
                  value,
                  newPassword: watchNewPassword,
                  passwordScore,
                  translator: t,
                })
              }
            />

            <Suspense fallback={<LoadingSpinner />}>
              <PasswordStrengthBar
                password={watchNewPassword}
                minLength={MINIMUM_PASSWORD_LENGTH}
                shortScoreWord={watchNewPassword ? t('passwordStrength.tooWeak') : ''}
                scoreWords={scoreWords}
                onChangeScore={(score) => {
                  if (watchNewPassword?.length && !showScore) {
                    setShowScore(true);
                  }
                  if (!watchNewPassword?.length && showScore) {
                    setShowScore(false);
                  }
                  setPasswordScore(score);
                }}
              />
            </Suspense>
          </div>

          <PasswordField
            register={register}
            onClearInput={resetField}
            hasError={Boolean(errors.confirmPassword)}
            errorMessage={errors.confirmPassword?.message}
            fieldName="confirmPassword"
            label={t('forgotPasswordScreen.confirmPassword')}
            showPlaceholder={false}
            margin="36px 0 0"
            onValidate={(value) =>
              handlePasswordValidation({
                value,
                newPassword: watchNewPassword,
                passwordScore,
                translator: t,
                isConfirmPasswordField: true,
              })
            }
          />
          <Button
            type="submit"
            label={t('account.submitPassword')}
            isFullWidth
            margin={`${screenSize === 'sm' ? '18px' : '32px'} 0 0`}
          />
        </form>
      </AuthBox>
    </AuthWrapper>
  );
}

export default CreatePassword;
